import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { ASSIST_WARN_BEFORE_MS, type OperatorCallView, type OperatorProfile } from '@360face/domain';
import { ApiError } from '../api/client';
import { MESSAGES, errorText } from '../i18n/messages';
import { mmss, secondsLeft } from '../v2/kiosk/assist';
import type { OperatorApi } from './api';
import { OperatorVideo } from './OperatorVideo';
import { OPERATOR_MESSAGES, type OperatorLocale, type OperatorMessages } from './messages';
import './operator.css';

const POLL_MS = 3_000;
const LANG_KEY = 'hub-operator-lang';

function initialLocale(): OperatorLocale {
  try {
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved === 'ru' || saved === 'pt' || saved === 'en') return saved;
  } catch {
    // storage blocked
  }
  const nav = (navigator.language || 'pt').slice(0, 2);
  return nav === 'ru' ? 'ru' : nav === 'en' ? 'en' : 'pt';
}

/**
 * Operator console at hub.360face.pt/operator (owner 20.10.2026). A kiosk call rings every online
 * operator of that studio; the first to answer takes it. The operator sees the customer's screen
 * only while connected, can extend the call and call a therapist; no remote actions on the kiosk.
 */
export function Operator({ api }: { api: OperatorApi }) {
  const [locale, setLocale] = useState<OperatorLocale>(initialLocale);
  const [loggedIn, setLoggedIn] = useState(api.loggedIn);
  const t = OPERATOR_MESSAGES[locale];
  const changeLocale = (l: OperatorLocale) => {
    setLocale(l);
    try {
      window.localStorage.setItem(LANG_KEY, l);
    } catch {
      // storage blocked: only for this visit
    }
  };
  return (
    <div className="op" lang={locale}>
      <header className="op-top">
        <strong>
          360<i>°</i>FACE
        </strong>
        <span>{t.title}</span>
        <span className="op-spacer" />
        <div className="op-langs" role="group">
          {(['ru', 'pt', 'en'] as const).map((l) => (
            <button key={l} aria-pressed={l === locale} onClick={() => changeLocale(l)}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        {loggedIn && (
          <button
            className="op-btn op-btn-quiet"
            onClick={() => {
              api.setOnline(false).catch(() => undefined);
              api.logout();
              setLoggedIn(false);
            }}
          >
            {t.signOut}
          </button>
        )}
      </header>
      {loggedIn ? (
        <Console api={api} t={t} locale={locale} onLoggedOut={() => setLoggedIn(false)} />
      ) : (
        <Login api={api} t={t} onDone={() => setLoggedIn(true)} />
      )}
    </div>
  );
}

function Login({ api, t, onDone }: { api: OperatorApi; t: OperatorMessages; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.login(email.trim(), pin);
      unlockSound();
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => undefined);
      }
      onDone();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(code === 'PIN_LOCKED' ? t.loginLocked : code === 'NETWORK' ? t.network : t.loginInvalid);
    } finally {
      setBusy(false);
      setPin('');
    }
  };
  return (
    <form className="op-login" onSubmit={submit}>
      <label>
        {t.email}
        <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label>
        {t.pin}
        <input
          type="password"
          inputMode="numeric"
          autoComplete="current-password"
          pattern="\d{4,8}"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          required
        />
      </label>
      {error && (
        <p className="op-error" role="alert">
          {error}
        </p>
      )}
      <button className="op-btn op-btn-red" disabled={busy}>
        {t.signIn}
      </button>
    </form>
  );
}

function Console({
  api,
  t,
  locale,
  onLoggedOut,
}: {
  api: OperatorApi;
  t: OperatorMessages;
  locale: OperatorLocale;
  onLoggedOut: () => void;
}) {
  const [me, setMe] = useState<OperatorProfile>();
  const [online, setOnline] = useState(true);
  const [calls, setCalls] = useState<OperatorCallView[]>([]);
  const [netError, setNetError] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [now, setNow] = useState(() => Date.now());
  const rang = useRef(new Set<string>());

  const failed = useCallback(
    (e: unknown) => {
      if (e instanceof ApiError && e.status === 401) onLoggedOut();
      else setNetError(true);
    },
    [onLoggedOut],
  );

  useEffect(() => {
    api.me().then((p) => {
      setMe(p);
    }, failed);
  }, [api, failed]);

  // Poll calls; the poll is also the heartbeat that keeps the operator online.
  useEffect(() => {
    let stop = false;
    const load = () =>
      api.calls(online).then(
        (list) => {
          if (stop) return;
          setNetError(false);
          setCalls(list);
          for (const c of list) {
            if (c.status === 'ringing' && online && !rang.current.has(c.id)) {
              rang.current.add(c.id);
              ring(t.notifyTitle, t.notifyBody(c.locationName));
            }
          }
        },
        (e) => !stop && failed(e),
      );
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [api, online, failed, t]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(undefined), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const toggleOnline = () => {
    const next = !online;
    setOnline(next);
    unlockSound();
    api.setOnline(next).catch(failed);
  };

  const replace = (c: OperatorCallView) => setCalls((list) => list.map((x) => (x.id === c.id ? c : x)));
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CALL_TAKEN') setNotice(t.callTaken);
      else failed(e);
    }
  };

  const mine = calls.find((c) => c.mine && c.status === 'active');
  const others = calls.filter((c) => c !== mine);

  return (
    <main className="op-main">
      <section className="op-status">
        <button
          className={online ? 'op-switch on' : 'op-switch'}
          role="switch"
          aria-checked={online}
          onClick={toggleOnline}
        >
          <span />
          {online ? t.online : t.offline}
        </button>
        <p>{online ? t.onlineHint : t.offlineHint}</p>
        {me && (
          <p className="op-me">
            {me.name} · {me.locations.map((l) => l.name).join(', ')} · {me.hours}
          </p>
        )}
      </section>

      {netError && (
        <p className="op-error" role="alert">
          {t.network}
        </p>
      )}
      {notice && (
        <p className="op-error" role="status">
          {notice}
        </p>
      )}

      {mine && (
        <ActiveCall
          call={mine}
          t={t}
          locale={locale}
          now={now}
          onExtend={() => act(async () => replace(await api.extend(mine.id)))}
          onTherapist={() =>
            act(async () => {
              await api.callTherapist(mine.id);
              setNotice(t.therapistSent);
            })
          }
          onEnd={() =>
            act(async () => {
              await api.end(mine.id);
              setCalls((list) => list.filter((x) => x.id !== mine.id));
            })
          }
        />
      )}

      <section className="op-list">
        {others.length === 0 && !mine && <p className="op-empty">{t.noCalls}</p>}
        {others.map((c) => (
          <article key={c.id} className={c.status === 'ringing' ? 'op-call ringing' : 'op-call'}>
            <div>
              <h3>{c.status === 'ringing' ? t.ringing : t.takenBy(c.operatorName ?? '')}</h3>
              <p>
                <b>{c.locationName}</b> · {t.language}: {t.langs[c.locale] ?? c.locale} · {t.waiting(c.waitingSec)}
              </p>
              <p className="op-mute">
                {t.calledFrom}: {t.steps[c.screen] ?? c.screen}
                {c.status === 'ringing' && c.stage === 'tablets' && ` · ${t.stageTablets}`}
                {c.status === 'ringing' && c.stage === 'whatsapp' && ` · ${t.stageWhatsapp}`}
              </p>
            </div>
            {c.status === 'ringing' && !mine && (
              <button className="op-btn op-btn-red" onClick={() => act(async () => replace(await api.answer(c.id)))}>
                {t.answer}
              </button>
            )}
          </article>
        ))}
      </section>
    </main>
  );
}

function ActiveCall({
  call,
  t,
  locale,
  now,
  onExtend,
  onTherapist,
  onEnd,
}: {
  call: OperatorCallView;
  t: OperatorMessages;
  locale: OperatorLocale;
  now: number;
  onExtend: () => void;
  onTherapist: () => void;
  onEnd: () => void;
}) {
  const left = secondsLeft(call.endsAt, now);
  const warn = left !== undefined && left * 1000 <= ASSIST_WARN_BEFORE_MS;
  const m = call.mirror;
  const rows: Array<[string, string | undefined]> = m
    ? [
        [t.field.step, t.steps[m.step] ?? m.step],
        [t.field.mode, m.mode ? (t.modes[m.mode] ?? m.mode) : undefined],
        [t.field.service, m.service],
        [t.field.when, [m.date, m.time].filter(Boolean).join(' ') || undefined],
        [t.field.staff, m.staff],
        [t.field.extras, m.extras ? String(m.extras) : undefined],
        [t.field.notice, m.notice ? errorText(MESSAGES[locale], m.notice) : undefined],
      ]
    : [];
  return (
    <section className="op-active">
      <div className="op-video">{call.join && <OperatorVideo join={call.join} title={call.locationName} />}</div>
      <aside className="op-side">
        <h2>{call.locationName}</h2>
        <p>
          {t.language}: <b>{t.langs[call.locale] ?? call.locale}</b>
        </p>
        {left !== undefined && (
          <p className={warn ? 'op-timer warn' : 'op-timer'} role={warn ? 'alert' : undefined}>
            {t.endsIn(mmss(left))}
          </p>
        )}
        <h3>{t.screenTitle}</h3>
        <dl className="op-mirror">
          {rows
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
        </dl>
        <div className="op-actions">
          <button className="op-btn" onClick={onExtend}>
            {t.extend}
          </button>
          <button className="op-btn" onClick={onTherapist}>
            {t.therapist}
          </button>
          <button className="op-btn op-btn-red" onClick={onEnd}>
            {t.end}
          </button>
        </div>
      </aside>
    </section>
  );
}

// ── ring: a short tone + a system notification when the tab is in the background ──

let audio: AudioContext | undefined;

/** Browsers allow sound only after a click: called from the login and the online switch. */
function unlockSound() {
  try {
    audio ??= new AudioContext();
    void audio.resume();
  } catch {
    // no Web Audio: notification only
  }
}

function ring(title: string, body: string) {
  try {
    if (audio) {
      const start = audio.currentTime;
      for (let i = 0; i < 3; i++) {
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.2, start + i * 0.6);
        gain.gain.setValueAtTime(0, start + i * 0.6 + 0.35);
        osc.connect(gain).connect(audio.destination);
        osc.start(start + i * 0.6);
        osc.stop(start + i * 0.6 + 0.4);
      }
    }
  } catch {
    // ignore
  }
  try {
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, tag: 'hub-operator-call' });
    }
  } catch {
    // ignore
  }
}
